import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { SignOut, LockKey } from "@phosphor-icons/react";

const TOKEN_KEY = "anxya_admin_token";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Dense = ({ cols, rows, testId }) => (
  <div className="rounded-lg border border-primary/10 bg-white overflow-x-auto" data-testid={testId}>
    <Table>
      <TableHeader>
        <TableRow>
          {cols.map((c) => <TableHead key={c} className="text-xs font-bold uppercase tracking-wider text-primary/60">{c}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow><TableCell colSpan={cols.length} className="text-sm text-muted-foreground py-10 text-center">No submissions yet.</TableCell></TableRow>
        ) : rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => <TableCell key={j} className="text-sm align-top max-w-[18rem] whitespace-pre-wrap">{cell}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const fmtError = (detail) => {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const LoginGate = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success(`Welcome back, ${data.user.name}.`);
    } catch (err) {
      setError(fmtError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-32 pb-32 min-h-screen flex items-start justify-center" data-testid="admin-login-page">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md rounded-3xl border border-primary/10 bg-white p-10 space-y-6 mt-8">
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/5 text-primary">
            <LockKey size={26} weight="duotone" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-medium tracking-tight text-primary">Admin Sign In</h1>
          <p className="mt-2 text-sm text-muted-foreground">Restricted to the Anxya Tech team.</p>
        </div>
        {error && (
          <p data-testid="admin-login-error" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">{error}</p>
        )}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="admin-email">Email</label>
          <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent" placeholder="admin@anxya.tech" />
        </div>
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="admin-password">Password</label>
          <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 h-12 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent" placeholder="••••••••" />
        </div>
        <Button type="submit" data-testid="admin-login-submit-btn" disabled={loading} className="w-full rounded-full bg-primary h-12 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20">
          {loading ? "Signing in…" : "Sign In"}
        </Button>
      </form>
    </section>
  );
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [enquiries, setEnquiries] = useState([]);
  const [demos, setDemos] = useState([]);
  const [apps, setApps] = useState([]);
  const [deckEvents, setDeckEvents] = useState([]);
  const [deckLeads, setDeckLeads] = useState([]);

  useEffect(() => {
    if (!token) return;
    Promise.all([api.get("/enquiries"), api.get("/demo-requests"), api.get("/applications"), api.get("/deck-events"), api.get("/deck-leads")])
      .then(([e, d, a, ev, l]) => { setEnquiries(e.data); setDemos(d.data); setApps(a.data); setDeckEvents(ev.data); setDeckLeads(l.data); })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          toast.error("Session expired. Please sign in again.");
        } else {
          toast.error("Could not load submissions.");
        }
      });
  }, [token]);

  if (!token) return <LoginGate onLogin={setToken} />;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success("Signed out.");
  };

  return (
    <section className="pt-28 pb-24 min-h-screen" data-testid="admin-page">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-accent">Admin</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">Submissions</h1>
            <p className="mt-2 text-sm text-muted-foreground">All enquiries, Talk to Us requests, and job applications received through the site.</p>
          </div>
          <Button onClick={logout} data-testid="admin-logout-btn" variant="outline" className="rounded-full border-primary/20 text-primary hover:border-accent shrink-0">
            <SignOut size={16} className="mr-2" /> Sign Out
          </Button>
        </div>

        <Tabs defaultValue="enquiries" className="mt-10">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="demos" data-testid="admin-tab-demos">Talk to Us ({demos.length})</TabsTrigger>
            <TabsTrigger value="applications" data-testid="admin-tab-applications">Applications ({apps.length})</TabsTrigger>
            <TabsTrigger value="deck" data-testid="admin-tab-deck">Deck Analytics ({deckEvents.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Deck Leads ({deckLeads.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="enquiries" className="mt-6">
            <Dense
              testId="admin-enquiries-table"
              cols={["Date", "Name", "Email", "Organization", "Service", "Message"]}
              rows={enquiries.map((e) => [fmt(e.created_at), e.name, e.email, e.organization || "—", e.service || "—", e.message])}
            />
          </TabsContent>
          <TabsContent value="demos" className="mt-6">
            <Dense
              testId="admin-demos-table"
              cols={["Date", "Name", "Email", "Organization", "Role", "Service", "Preferred Date", "Ref", "Notes"]}
              rows={demos.map((d) => [fmt(d.created_at), d.name, d.email, d.organization, d.role || "—", d.service || "—", d.preferred_date || "—", d.ref || "—", d.message || "—"])}
            />
          </TabsContent>
          <TabsContent value="applications" className="mt-6">
            <Dense
              testId="admin-applications-table"
              cols={["Date", "Name", "Email", "Role", "LinkedIn", "Cover Note"]}
              rows={apps.map((a) => [fmt(a.created_at), a.name, a.email, a.job_title, a.linkedin || "—", a.cover_note || "—"])}
            />
          </TabsContent>
          <TabsContent value="deck" className="mt-6">
            <div className="grid gap-4 sm:grid-cols-3 mb-6" data-testid="deck-stats">
              <div className="rounded-xl border border-primary/10 bg-white p-6">
                <p className="font-display text-3xl font-semibold text-primary">{deckEvents.filter((e) => e.type === "view").length}</p>
                <p className="mt-1 text-xs text-muted-foreground">Deck views</p>
              </div>
              <div className="rounded-xl border border-primary/10 bg-white p-6">
                <p className="font-display text-3xl font-semibold text-primary">{deckEvents.filter((e) => e.type === "print").length}</p>
                <p className="mt-1 text-xs text-muted-foreground">Printed / downloaded as PDF</p>
              </div>
              <div className="rounded-xl border border-primary/10 bg-white p-6">
                <p className="font-display text-3xl font-semibold text-primary">{new Set(deckEvents.map((e) => e.timezone).filter(Boolean)).size}</p>
                <p className="mt-1 text-xs text-muted-foreground">Distinct locations (by timezone)</p>
              </div>
            </div>
            <Dense
              testId="admin-deck-table"
              cols={["Date", "Action", "Location (timezone)", "Came From"]}
              rows={deckEvents.map((e) => [fmt(e.created_at), e.type === "print" ? "Printed / PDF" : "Viewed", e.timezone || "—", e.referrer || "direct"])}
            />
          </TabsContent>
          <TabsContent value="leads" className="mt-6">
            <Dense
              testId="admin-leads-table"
              cols={["Date", "Name", "Title", "Company", "Corporate Email", "Ref"]}
              rows={deckLeads.map((l) => [fmt(l.created_at), l.name, l.title, l.company, l.email, l.ref || "—"])}
            />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
